import type { BoardBackgroundRef, IllustId } from '../consts/backgroundCatalog';
import type { BoardTemplate } from '../types/template';

/**
 * 보드 템플릿 프리셋. 문구는 i18n 키(boardTemplates ns)로 참조 — ko/en 원문은 시트/locale 관리.
 * emoji = native unicode. 렌더는 t(nameKey)/t(descriptionKey), 생성 시 t()로 평문 해석.
 */
export const BOARD_EMOJI_CANDIDATES = [
  '📌',
  '📝',
  '💡',
  '✅',
  '⭐',
  '📚',
  '🎯',
  '🔎',
  '💬',
  '🏫',
] as const;

export const DEFAULT_BOARD_EMOJI = '📝';

/**
 * 템플릿 없이 빈 보드로 시작할 때 뽑는 배경 풀. 유형(WALL/GROUP/TIMELINE) 구분 없이 공용이다.
 *
 * 선정 기준은 두 가지다.
 * 1. **밝기** — 썸네일 실측 지각 밝기(L*)가 78 이상인 것만 담았다. 어두운 6종
 *    (SEOUL_NIGHT·HALLOWEEN·SPACE·CHALKBOARD·STADIUM·MYSTIC_FOREST, L* 22~62)은
 *    62↔78 사이에 뚜렷한 절벽이 있어 갈랐다. 카드가 얹히는 캔버스라 밝은 쪽이 안전하다.
 * 2. **시즌 중립** — 크리스마스·운동회·생일처럼 시기가 박힌 배경은 뺐다. 8월에 새 보드를
 *    만들었는데 눈밭이 깔리면 배경을 바로 바꾸게 된다. CHERRY_BLOSSOM 은 봄이 읽히긴 해도
 *    특정 행사가 아니라 남겼다.
 *
 * 이 값을 안 보내면 서버가 background 를 null 로 두고, FE 가 backgroundColor 기본 hex 로
 * 폴백해 전부 PURPLE 단색이 된다 — 빈 보드가 전부 똑같아 보이던 이유다.
 *
 * 순서는 카탈로그 순서를 따른다(랜덤 추출이라 노출 순서와 무관, 대조하기 쉬우라고).
 */
export const BLANK_BOARD_ILLUSTS = [
  'CLASSROOM_DESK',
  'CLASSROOM_GROUP',
  'SCHOOL_HALLWAY',
  'LIBRARY',
  'GYM',
  'GALLERY',
  'SKY_ROOFTOP',
  'CHERRY_BLOSSOM',
  'GREEN_FIELD',
  'HANOK',
  'TEMPLE',
  'TRAIN_STATION',
  'MUSIC',
] as const satisfies readonly IllustId[];

/**
 * 빈 보드 생성 시 심을 배경 하나를 고른다. 직전에 뽑힌 값을 기억하지 않는다 —
 * 연속으로 같은 배경이 나올 수 있지만, 13종이면 체감 반복이 낮고 상태를 들 이유가 없다.
 */
export const getRandomBlankBoardBackground = (): BoardBackgroundRef => ({
  type: 'ILLUST',
  id:
    BLANK_BOARD_ILLUSTS[
      Math.floor(Math.random() * BLANK_BOARD_ILLUSTS.length)
    ] ?? BLANK_BOARD_ILLUSTS[0],
});

export const getRandomBoardEmoji = () =>
  BOARD_EMOJI_CANDIDATES[
    Math.floor(Math.random() * BOARD_EMOJI_CANDIDATES.length)
  ] ?? DEFAULT_BOARD_EMOJI;

/**
 * 순서 = 수업 직후 바로 쓰는 것 우선(배움 정리·생각 나누기·작품 공유 → 학급 운영 → 토의·점검 → 관계).
 * descriptionKey = 선생님이 템플릿을 고를 때 보는 소개, noticeKey = 학생에게 보이는 안내문.
 * emoji = 템플릿 테마에 맞춰 자유 선택(BOARD_EMOJI_CANDIDATES 풀과 무관 — 그건 새 빈 보드용).
 * 단, 피부색/ZWJ 조합 emoji는 쓰지 않고 macOS/Windows 모두 안정적으로 렌더되는 기본 emoji만 사용.
 * 각 템플릿 자체 groups emoji와도 겹치지 않게 선택.
 */
export const BOARD_TEMPLATES: BoardTemplate[] = [
  {
    id: 'lesson-reflection',
    background: { type: 'ILLUST', id: 'GREEN_FIELD' },
    type: 'GROUP',
    nameKey: 'lessonReflection.name',
    emoji: '📖',
    thumbnail: '/assets/board-template-lesson-reflection.webp',
    descriptionKey: 'lessonReflection.description',
    groups: [
      { nameKey: 'lessonReflection.group.learned', emoji: '💡' },
      { nameKey: 'lessonReflection.group.wonder', emoji: '❓' },
      { nameKey: 'lessonReflection.group.explore', emoji: '🔎' },
    ],
    noticeKey: 'lessonReflection.notice',
  },
  {
    id: 'idea-sharing',
    background: { type: 'ILLUST', id: 'SKY_ROOFTOP' },
    type: 'WALL',
    nameKey: 'ideaSharing.name',
    emoji: '💭',
    thumbnail: '/assets/board-template-idea-sharing.webp',
    descriptionKey: 'ideaSharing.description',
    groups: [],
    noticeKey: 'ideaSharing.notice',
  },
  {
    id: 'showcase',
    background: { type: 'ILLUST', id: 'GALLERY' },
    type: 'WALL',
    nameKey: 'showcase.name',
    emoji: '🖼️',
    thumbnail: '/assets/board-template-showcase.webp',
    descriptionKey: 'showcase.description',
    groups: [],
    noticeKey: 'showcase.notice',
  },
  {
    id: 'self-intro',
    background: { type: 'ILLUST', id: 'CLASSROOM_DESK' },
    type: 'WALL',
    nameKey: 'selfIntro.name',
    emoji: '👋',
    thumbnail: '/assets/board-template-self-intro.webp',
    descriptionKey: 'selfIntro.description',
    groups: [],
    noticeKey: 'selfIntro.notice',
  },
  {
    id: 'class-notice',
    background: { type: 'ILLUST', id: 'CLASSROOM_DESK' },
    type: 'WALL',
    nameKey: 'classNotice.name',
    emoji: '🔔',
    thumbnail: '/assets/board-template-class-notice.webp',
    descriptionKey: 'classNotice.description',
    groups: [],
    noticeKey: 'classNotice.notice',
  },
  {
    id: 'debate',
    background: { type: 'ILLUST', id: 'OX_QUIZ' },
    type: 'GROUP',
    nameKey: 'debate.name',
    emoji: '⚖️',
    thumbnail: '/assets/board-template-debate.webp',
    descriptionKey: 'debate.description',
    groups: [
      { nameKey: 'debate.group.agree', emoji: '👍' },
      { nameKey: 'debate.group.disagree', emoji: '👎' },
      { nameKey: 'debate.group.undecided', emoji: '🤔' },
    ],
    noticeKey: 'debate.notice',
  },
  {
    id: 'traffic-light',
    background: { type: 'ILLUST', id: 'CLASSROOM_GROUP' },
    type: 'GROUP',
    nameKey: 'trafficLight.name',
    emoji: '🚦',
    thumbnail: '/assets/board-template-traffic-light.webp',
    descriptionKey: 'trafficLight.description',
    groups: [
      { nameKey: 'trafficLight.group.clear', emoji: '🟢' },
      { nameKey: 'trafficLight.group.confused', emoji: '🟡' },
      { nameKey: 'trafficLight.group.help', emoji: '🔴' },
    ],
    noticeKey: 'trafficLight.notice',
  },
  {
    id: 'kwl',
    background: { type: 'ILLUST', id: 'CHALKBOARD' },
    type: 'GROUP',
    nameKey: 'kwl.name',
    emoji: '🗂️',
    thumbnail: '/assets/board-template-kwl.webp',
    descriptionKey: 'kwl.description',
    groups: [
      { nameKey: 'kwl.group.know', emoji: '🧠' },
      { nameKey: 'kwl.group.want', emoji: '❓' },
      { nameKey: 'kwl.group.learned', emoji: '✅' },
    ],
    noticeKey: 'kwl.notice',
  },
  {
    id: 'praise-board',
    background: { type: 'ILLUST', id: 'CHERRY_BLOSSOM' },
    type: 'WALL',
    nameKey: 'praiseBoard.name',
    emoji: '👏',
    thumbnail: '/assets/board-template-praise-board.webp',
    descriptionKey: 'praiseBoard.description',
    groups: [],
    noticeKey: 'praiseBoard.notice',
  },
  {
    id: 'birthday-board',
    // 케이크 테이블이 있는 실내 파티. 야외 풍선 언덕은 BALLOON_HILL 다.
    background: { type: 'ILLUST', id: 'BIRTHDAY_PARTY' },
    type: 'WALL',
    nameKey: 'birthdayBoard.name',
    emoji: '🎂',
    thumbnail: '/assets/board-template-birthday-board.webp',
    descriptionKey: 'birthdayBoard.description',
    groups: [],
    noticeKey: 'birthdayBoard.notice',
  },
];
